/**
 * Page Completion Card
 * 
 * Shown when all experience videos on a page have been watched.
 * Asks the user whether to keep or hide the "Zo werkt SuperKonnected" button
 * for this specific page.
 */

import React, { useState, useEffect } from 'react';
import { useExperienceMode } from './ExperienceModeProvider';

const PAGE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  netwerk: 'Netwerk',
  agenda: 'Agenda',
  inbox: 'Inbox',
  vibes: 'Vibes',
  'social-media': 'Social Media',
  profiel: 'Profiel',
  training: 'Training Centre',
};

export const PageCompletionCard: React.FC = () => {
  const { pageJustCompleted, hidePageButton, keepPageButton, dismissPageCompletion } = useExperienceMode();
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (pageJustCompleted) {
      // Small delay before showing to let the video player close first
      const timer = setTimeout(() => setIsVisible(true), 600);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setIsFadingOut(false);
    }
  }, [pageJustCompleted]);

  if (!pageJustCompleted || !isVisible) return null;

  const pageLabel = PAGE_LABELS[pageJustCompleted] || pageJustCompleted;

  const handleHide = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      hidePageButton(pageJustCompleted);
    }, 400);
  };

  const handleKeep = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      keepPageButton(pageJustCompleted);
    }, 400);
  };

  return (
    <div
      className={`page-completion-overlay ${isFadingOut ? 'fade-out' : ''}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10020,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        animation: isFadingOut ? 'completionFadeOut 0.4s ease forwards' : 'completionFadeIn 0.5s ease forwards',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleKeep();
      }}
    >
      <div
        className="page-completion-card"
        style={{
          backgroundColor: 'white',
          borderRadius: '1.25rem',
          padding: '2rem',
          maxWidth: '380px',
          width: '90%',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1)',
          animation: isFadingOut 
            ? 'completionCardOut 0.4s ease forwards' 
            : 'completionCardIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          textAlign: 'center',
        }}
      >
        {/* Completion Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.25rem',
          fontSize: '1.75rem',
        }}>
          ✓
        </div>

        {/* Title */}
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '0.5rem',
        }}>
          Alle video's bekeken! 🎉
        </h3>

        {/* Description */}
        <p style={{
          fontSize: '0.875rem',
          color: '#6b7280',
          lineHeight: '1.5',
          marginBottom: '1.5rem',
        }}>
          Je hebt alle uitleg voor <strong>{pageLabel}</strong> bekeken.
          Wil je de <em>"Zo werkt SuperKonnected"</em> knop zichtbaar houden
          op deze pagina?
        </p>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
        }}>
          {/* Hide for this page */}
          <button
            onClick={handleHide}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              borderRadius: '0.75rem',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              color: '#374151',
              fontSize: '0.8125rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            Verberg hier
          </button>

          {/* Keep visible */}
          <button
            onClick={handleKeep}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              borderRadius: '0.75rem',
              border: 'none',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              fontSize: '0.8125rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Laat zichtbaar ✓
          </button>
        </div>

        {/* Subtle note */}
        <p style={{
          fontSize: '0.6875rem',
          color: '#9ca3af',
          marginTop: '1rem',
          lineHeight: '1.4',
        }}>
          Je kunt dit later altijd wijzigen. Op andere pagina's blijft de knop zichtbaar.
        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes completionFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes completionFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes completionCardIn {
          from { 
            opacity: 0; 
            transform: scale(0.85) translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        @keyframes completionCardOut {
          from { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
          to { 
            opacity: 0; 
            transform: scale(0.9) translateY(10px); 
          }
        }
      `}</style>
    </div>
  );
};
